import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { resolveUploadUrl } from "../../../../../infrastructure/utils/resolveUploadUrl";
import AssignmentCaseInfoPanel, {
  getCaseContactActions,
  getCaseReferenceId,
} from "./AssignmentCaseInfoPanel";

const display = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return null;
};

const isImagePath = (path) =>
  /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(path || ""));

const AttachmentPreview = ({ title, fileUrl, baseUrl }) => {
  if (!fileUrl) return null;
  const href = resolveUploadUrl(fileUrl, baseUrl);
  if (!href) return null;
  const showImage = isImagePath(fileUrl) || isImagePath(href);

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "grey.50",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      {showImage ? (
        <Box
          component="img"
          src={href}
          alt={title}
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: 320,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            mb: 1,
            objectFit: "contain",
            bgcolor: "background.paper",
          }}
        />
      ) : null}
      <Typography variant="body2">
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
          مشاهده / دانلود فایل
        </Link>
      </Typography>
    </Box>
  );
};

const AssignmentCaseInfoDialog = ({
  open,
  onClose,
  item,
  canSchedule = false,
  onScheduleVisit,
  siteVisitApiBase = "SLN/Supervisor/SiteVisit",
}) => {
  const [visitAttachment, setVisitAttachment] = useState({
    loading: false,
    url: "",
    baseUrl: "",
    error: "",
  });

  const siteVisitId =
    item?._meta?.relatedSiteVisitId ||
    pick(item, "relatedSiteVisitId", "RelatedSiteVisitId");

  useEffect(() => {
    let cancelled = false;

    const loadVisitAttachment = async () => {
      if (!open || !siteVisitId) {
        setVisitAttachment({ loading: false, url: "", baseUrl: "", error: "" });
        return;
      }

      const existingUrl = pick(
        item,
        "relatedSiteVisitAttachedFileUrl",
        "RelatedSiteVisitAttachedFileUrl"
      );
      const fallbackBaseUrl = String(window.globalConfig?.API_URL || "").trim();

      // اگر از لیست ارجاع‌ها URL آماده باشد، همان را نشان بده.
      if (existingUrl) {
        setVisitAttachment({
          loading: false,
          url: existingUrl,
          baseUrl: fallbackBaseUrl,
          error: "",
        });
        return;
      }

      setVisitAttachment((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const res = await DefaultApiService.getOneApi(
          siteVisitApiBase,
          siteVisitId
        );

        if (res?.isSuccess === false) {
          if (!cancelled) {
            setVisitAttachment({
              loading: false,
              url: "",
              baseUrl: fallbackBaseUrl,
              error: res?.message || "بارگذاری فایل پیوست بازدید ناموفق بود.",
            });
          }
          return;
        }

        const payload = res?.data ?? res?.Data ?? {};
        const visit = payload.siteVisit || payload.SiteVisit || {};
        const url = pick(visit, "attachedFileUrl", "AttachedFileUrl") || "";
        const baseUrl =
          pick(payload, "baseUrl", "BaseUrl") || fallbackBaseUrl;

        if (!cancelled) {
          setVisitAttachment({
            loading: false,
            url,
            baseUrl,
            error: "",
          });
        }
      } catch {
        if (!cancelled) {
          setVisitAttachment({
            loading: false,
            url: "",
            baseUrl: fallbackBaseUrl,
            error: "بارگذاری فایل پیوست بازدید ناموفق بود.",
          });
        }
      }
    };

    loadVisitAttachment();
    return () => {
      cancelled = true;
    };
  }, [open, siteVisitId, item, siteVisitApiBase]);

  if (!item) return null;

  const referenceId = getCaseReferenceId(item);
  const { mobile, mapUrl } = getCaseContactActions(item);
  const billImageUrl = pick(item, "electricBoillUrl", "ElectricBoillUrl");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" component="span">
            اطلاعات پرونده برای هماهنگی بازدید
          </Typography>
          <Chip size="small" label={`پیگیری ${display(referenceId)}`} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <AssignmentCaseInfoPanel item={item} dense />

        {visitAttachment.loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              در حال بارگذاری فایل پیوست بازدید…
            </Typography>
          </Stack>
        ) : null}

        {visitAttachment.error ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {visitAttachment.error}
          </Alert>
        ) : null}

        <AttachmentPreview
          title="فرم اسکن‌شده بازدید"
          fileUrl={visitAttachment.url}
          baseUrl={visitAttachment.baseUrl}
        />

        {!visitAttachment.loading && !visitAttachment.url && siteVisitId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            هنوز فایل فرم بازدید برای این پرونده آپلود نشده است.
          </Typography>
        ) : null}

        <AttachmentPreview
          title="تصویر قبض برق متقاضی"
          fileUrl={billImageUrl}
          baseUrl={visitAttachment.baseUrl || String(window.globalConfig?.API_URL || "").trim()}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {mobile ? (
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              href={`tel:${mobile}`}
            >
              تماس با متقاضی
            </Button>
          ) : null}
          {mapUrl ? (
            <Button
              variant="outlined"
              startIcon={<PlaceIcon />}
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              نقشه
            </Button>
          ) : null}
          {canSchedule && typeof onScheduleVisit === "function" ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EventAvailableIcon />}
              onClick={onScheduleVisit}
            >
              ثبت زمان بازدید
            </Button>
          ) : null}
        </Stack>
        <Button onClick={onClose} variant="outlined">
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentCaseInfoDialog;
